/**
 * Typed-form field definitions, growing type by type as each shape gets a
 * form: play, tts, hangup, transfer, consent, tag (Slice AI, flat fields
 * only); repeat and http (Slice AJ, adding the "rows" kind for http's
 * `response_mappings`); branch (Slice AK, adding a `"select"` row-field
 * kind and an `optional` row field for `cases`' own `value`, which is not
 * required — `operator: "exists"` never needs one); gather (Slice AL,
 * adding the `"prompt"` kind for its nested audio/tts `prompt` oneOf — a
 * kind toggle plus whichever sub-fields that kind needs, not another flat
 * row). `start` and `amd_gate` are never in this table either: start's
 * config is always `{}`, and amd_gate is excluded from the palette
 * entirely (nodeTemplates.ts). Every node type the schema declares now has
 * a typed form.
 *
 * `draftFromConfig`/`configFromDraft` are the pure halves of the form.
 * Every flat field's draft value is a string (every HTML control's value is
 * a string regardless of the field's real JSON type); a `rows` field's
 * draft value is an array of such all-string row objects, one per
 * `rowFields` entry; a `prompt` field's draft value is a `PromptDraft` — one
 * of each possible sub-field (`asset_id`, `text`, `voice`, `language`),
 * all strings, so switching the `kind` toggle back and forth never loses
 * whatever was typed into the other shape's fields. `configFromDraft` is
 * where each is turned back into the shape the schema expects — for
 * `prompt`, only the fields the *selected* kind actually needs. Neither
 * function validates against the schema — that is still validateFlow.ts's
 * job, surfaced by the existing diagnostics panel.
 */

export type FieldKind = "text" | "textarea" | "number" | "select" | "tags" | "rows" | "prompt";

export interface RowFieldDef {
  key: string;
  label: string;
  kind: "text" | "number" | "select";
  options?: string[];
  /** Omit this row field from the built row object when its draft is blank. */
  optional?: boolean;
}

export interface FieldDef {
  key: string;
  label: string;
  kind: FieldKind;
  options?: string[];
  /** Omit this key from the built config when the draft value is blank. */
  optional?: boolean;
  /** Only for kind "rows": the fields each row has. */
  rowFields?: RowFieldDef[];
}

export type RowDraft = Record<string, string>;

export interface PromptDraft {
  kind: "audio" | "tts";
  asset_id: string;
  text: string;
  voice: string;
  language: string;
}

export type FieldValue = string | RowDraft[] | PromptDraft;
export type Draft = Record<string, FieldValue>;

export function emptyPrompt(): PromptDraft {
  return { kind: "audio", asset_id: "", text: "", voice: "", language: "" };
}

export const TYPED_NODE_FIELDS: Record<string, FieldDef[]> = {
  play: [{ key: "asset_id", label: "Asset ID", kind: "text" }],
  tts: [
    { key: "text", label: "Text", kind: "textarea" },
    { key: "voice", label: "Voice", kind: "text" },
    { key: "language", label: "Language", kind: "text" },
  ],
  hangup: [
    { key: "reason", label: "Reason", kind: "select", options: ["normal", "error", "policy", "budget"] },
  ],
  transfer: [
    { key: "destination", label: "Destination (E.164)", kind: "text" },
    { key: "timeout_ms", label: "Timeout (ms)", kind: "number" },
  ],
  consent: [
    { key: "action", label: "Action", kind: "select", options: ["opt_in", "opt_out"] },
    { key: "channel", label: "Channel", kind: "select", options: ["voice", "sms"] },
    { key: "purpose", label: "Purpose", kind: "text" },
  ],
  tag: [
    { key: "tags", label: "Tags (comma-separated)", kind: "tags" },
    { key: "disposition", label: "Disposition", kind: "text", optional: true },
    { key: "event_type", label: "Event type", kind: "text", optional: true },
  ],
  repeat: [
    { key: "trigger_keys", label: "Trigger keys (comma-separated)", kind: "tags" },
    { key: "max_repeats", label: "Max repeats", kind: "number" },
  ],
  http: [
    { key: "endpoint_id", label: "Endpoint ID", kind: "text" },
    { key: "method", label: "Method", kind: "select", options: ["GET", "POST"] },
    { key: "timeout_ms", label: "Timeout (ms)", kind: "number" },
    {
      key: "response_mappings",
      label: "Response mappings",
      kind: "rows",
      rowFields: [
        { key: "json_pointer", label: "JSON pointer", kind: "text" },
        { key: "variable", label: "Variable", kind: "text" },
      ],
    },
  ],
  branch: [
    {
      key: "cases",
      label: "Cases",
      kind: "rows",
      rowFields: [
        { key: "id", label: "Case ID", kind: "text" },
        { key: "variable", label: "Variable", kind: "text" },
        {
          key: "operator",
          label: "Operator",
          kind: "select",
          options: ["eq", "ne", "gt", "gte", "lt", "lte", "exists"],
        },
        { key: "value", label: "Value", kind: "text", optional: true },
      ],
    },
  ],
  gather: [
    { key: "valid_keys", label: "Valid keys (comma-separated)", kind: "tags" },
    { key: "timeout_ms", label: "Timeout (ms)", kind: "number" },
    { key: "retries", label: "Retries", kind: "number" },
    { key: "prompt", label: "Prompt", kind: "prompt" },
  ],
};

export function emptyRow(field: FieldDef): RowDraft {
  const row: RowDraft = {};
  for (const rowField of field.rowFields ?? []) {
    row[rowField.key] = rowField.kind === "select" ? (rowField.options?.[0] ?? "") : "";
  }
  return row;
}

export function draftFromConfig(fields: FieldDef[], config: unknown): Draft {
  const source = config && typeof config === "object" ? (config as Record<string, unknown>) : {};
  const draft: Draft = {};
  for (const field of fields) {
    const value = source[field.key];
    if (field.kind === "tags") {
      draft[field.key] = Array.isArray(value)
        ? value.filter((item): item is string => typeof item === "string").join(", ")
        : "";
      continue;
    }
    if (field.kind === "rows") {
      const rowFields = field.rowFields ?? [];
      draft[field.key] = Array.isArray(value)
        ? value.map((item) => {
            const rowSource = item && typeof item === "object" ? (item as Record<string, unknown>) : {};
            const row: RowDraft = {};
            for (const rowField of rowFields) {
              const rowValue = rowSource[rowField.key];
              row[rowField.key] = rowValue === undefined || rowValue === null ? "" : String(rowValue);
            }
            return row;
          })
        : [];
      continue;
    }
    if (field.kind === "prompt") {
      const promptSource = value && typeof value === "object" ? (value as Record<string, unknown>) : {};
      const stringOf = (key: string) => (typeof promptSource[key] === "string" ? (promptSource[key] as string) : "");
      draft[field.key] = {
        kind: promptSource.kind === "tts" ? "tts" : "audio",
        asset_id: stringOf("asset_id"),
        text: stringOf("text"),
        voice: stringOf("voice"),
        language: stringOf("language"),
      } satisfies PromptDraft;
      continue;
    }
    draft[field.key] = value === undefined || value === null ? "" : String(value);
  }
  return draft;
}

export function configFromDraft(fields: FieldDef[], draft: Draft): Record<string, unknown> {
  const config: Record<string, unknown> = {};
  for (const field of fields) {
    if (field.kind === "rows") {
      const rows = Array.isArray(draft[field.key]) ? (draft[field.key] as RowDraft[]) : [];
      const rowFields = field.rowFields ?? [];
      config[field.key] = rows.map((row) => {
        const built: Record<string, unknown> = {};
        for (const rowField of rowFields) {
          const raw = row[rowField.key] ?? "";
          if (rowField.optional && raw.trim() === "") {
            continue;
          }
          if (rowField.kind === "number") {
            const parsed = Number(raw);
            built[rowField.key] = Number.isFinite(parsed) && raw.trim() !== "" ? parsed : raw;
          } else {
            built[rowField.key] = raw;
          }
        }
        return built;
      });
      continue;
    }
    if (field.kind === "prompt") {
      const promptDraft = draft[field.key];
      const p =
        promptDraft && typeof promptDraft === "object" && !Array.isArray(promptDraft)
          ? (promptDraft as PromptDraft)
          : emptyPrompt();
      config[field.key] =
        p.kind === "tts"
          ? { kind: "tts", text: p.text, voice: p.voice, language: p.language }
          : { kind: "audio", asset_id: p.asset_id };
      continue;
    }
    const raw = typeof draft[field.key] === "string" ? (draft[field.key] as string) : "";
    if (field.kind === "tags") {
      config[field.key] = raw
        .split(",")
        .map((item) => item.trim())
        .filter((item) => item.length > 0);
      continue;
    }
    if (field.optional && raw.trim() === "") {
      continue;
    }
    if (field.kind === "number") {
      const parsed = Number(raw);
      config[field.key] = Number.isFinite(parsed) && raw.trim() !== "" ? parsed : raw;
      continue;
    }
    config[field.key] = raw;
  }
  return config;
}
